import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  effect,
  inject,
  ViewChild,
  ElementRef,
  afterNextRender,
  Injector,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { HardwareVariant, HardwareVariantGroup } from '../../models/mobilephone-variant';
import {
  AddMobilePhoneReplacementAction,
  AddNewMobilePhoneAction,
  RemoveMobileProductAction,
} from '@sales/states/customer-product-mobilephone/customer-product-mobilephone.actions';
import { SearchDisplayType, MobilePhoneAvailability } from '../../models/mobilephone-search-enums';
import {
  ApiMobilephoneSearchAction,
  ApiMobilephoneFiltersAction,
} from '../../states/mobilephone-search/mobilephone-search.actions';
import { IconNames } from '@shared/enums/icon-names';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { CustomerProductMobilePhoneSelectors } from '@sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { MobilePhoneDetails, MobilephoneDetailsComponent } from './mobilephone-details/mobilephone-details.component';
import { CustomerProductSelectors } from '@sales/states/customer-product/customer-product.selectors';
import { ThemeService } from '@core/services/theme.service';
import { Theme } from '@support/models/theme.model';
import { MobilephoneSearchHelperService } from '../../services/mobilephone-search-helper.service';
import { MobilephoneSearchService } from '../../services/mobilephone-search.service';
import { NotificationService } from '@app/core/services/notification.service';
import { MobilePhoneSearchSelectors } from '../../states/mobilephone-search/mobilephone-search.selectors';
import { AsyncPipe } from '@angular/common';
import { HardwareSearchResultComponent } from '@app/shared/components/hardware-search-result/hardware-search-result.component';
import { MobilephoneSearchFilterComponent } from './mobilephone-search-filter/mobilephone-search-filter.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MobilephoneSearchHeaderComponent } from './mobilephone-search-header/mobilephone-search-header.component';

@Component({
  selector: 'app-mobilephone-search',
  templateUrl: './mobilephone-search.component.html',
  styleUrls: ['./mobilephone-search.component.scss'],
  imports: [
    MobilephoneSearchHeaderComponent,
    CdkScrollable,
    MatDialogContent,
    MobilephoneSearchFilterComponent,
    HardwareSearchResultComponent,
    MobilephoneDetailsComponent,
    AsyncPipe,
  ],
})
export class MobilephoneSearchComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private injector = inject(Injector);
  dialogRef = inject<MatDialogRef<MobilephoneSearchComponent>>(MatDialogRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);
  private mobilePhoneSearchService = inject(MobilephoneSearchService);
  private mobilePhoneSearchHelperService = inject(MobilephoneSearchHelperService);
  private notificationService = inject(NotificationService);
  data = inject<{
    mobileProductInCart: MobilePhoneProduct;
    isManualRegistration: boolean;
    mobilePhoneVariants: HardwareVariant[];
  }>(MAT_DIALOG_DATA);

  IconNames = IconNames;

  mobilephoneSearch = select(MobilePhoneSearchSelectors.getState);
  isEquipmentUpgradeEnabled = select(DealerStateSelectors.isEquipmentUpgradeEnabled);
  mobilephoneProducts = select(CustomerProductMobilePhoneSelectors.mobilePhoneProducts);
  allVoiceProducts = select(CustomerProductSelectors.getAllVoiceProducts);
  canSellHardwareWithLogistics = select(DealerStateSelectors.canSellHardwareWithLogistics);
  canSellCellPhoneWithSubscription = select(DealerStateSelectors.canSellCellPhoneWithSubscription);
  canSellCellPhoneWithoutSubscription = select(DealerStateSelectors.canSellCellPhoneWithoutSubscription);

  selectedMobilePhoneVariant: HardwareVariant = null;
  displayType = SearchDisplayType.Grid;
  searchText: string = '';
  selectedManufacturers: string[] = [];
  selectedCategories: string[] = [];
  selectedAvailableForList: MobilePhoneAvailability[] = [];
  allMobilePhoneVariantGroups: HardwareVariantGroup[] = [];
  filteredMobilePhoneVariantGroups: HardwareVariantGroup[];

  loadingGroupVariantsForGroup: string = null;
  isLoadingSearchResults: boolean = false;

  hardwareSearchScrollPosition: number = 0;
  @ViewChild('dialogContent') dialogContent: ElementRef<HTMLDivElement>;

  manufacturers: string[] = [];
  categories: string[] = [];
  isLoadingFilters: boolean = false;
  selectedTheme$: Observable<Theme>;
  showOnlyEquipmentUpgradeCompatibleOfferings: boolean;
  selectedGroupVariants: HardwareVariant[] = null;

  subscriptions$: Subscription[] = [];

  constructor() {
    effect(() => {
      this.allMobilePhoneVariantGroups = this.mobilephoneSearch()?.searchResults;
      this.setSelectedItemInSearchResults();
      this.isLoadingSearchResults = this.mobilephoneSearch()?.isLoadingSearchResults;
      this.isLoadingFilters = this.mobilephoneSearch()?.isLoadingMobilephoneFilters;
      this.manufacturers = this.mobilephoneSearch()?.mobilePhoneFilters?.manufacturers || [];
      this.categories = this.mobilephoneSearch()?.mobilePhoneFilters?.categories || [];
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnInit() {
    if (this.data.mobilePhoneVariants) {
      this.selectedGroupVariants = this.data.mobilePhoneVariants;
    }

    this.selectedTheme$ = this.themeService.selectedTheme;

    this.showOnlyEquipmentUpgradeCompatibleOfferings = !!(
      this.data.mobileProductInCart?.existingAgreementId ||
      (this.isEquipmentUpgradeEnabled() && !this.canSellCellPhoneWithSubscription())
    );

    if (!this.data.isManualRegistration) {
      this.store.dispatch([new ApiMobilephoneSearchAction(), new ApiMobilephoneFiltersAction()]);
    }

    if (this.data.mobileProductInCart?.mobilePhoneVariant) {
      this.selectedMobilePhoneVariant = this.data.mobileProductInCart.mobilePhoneVariant;
    }
  }

  onAddMobileProduct({
    mobilePhoneOffering,
    mobilePhoneDetails,
  }: {
    mobilePhoneOffering: HardwareVariant;
    mobilePhoneDetails: MobilePhoneDetails;
  }) {
    if (this.data.mobileProductInCart) {
      this.updateMobileProductAlreadyInCart(mobilePhoneDetails, mobilePhoneOffering);
    } else {
      this.subscriptions$.push(
        this.store
          .dispatch(new AddNewMobilePhoneAction(mobilePhoneOffering, mobilePhoneDetails, this.data.isManualRegistration))
          .subscribe(() => {
            const openNewVoiceSubscription = mobilePhoneDetails.newOfferingId && !mobilePhoneDetails.isSubscriptionUpgrade;
            this.dialogRef.close(openNewVoiceSubscription);
          })
      );
    }
  }

  private updateMobileProductAlreadyInCart(mobilePhoneDetails: MobilePhoneDetails, mobilePhoneOffering: HardwareVariant) {
    const connectedVoiceProductInCart = this.allVoiceProducts().find(
      v => v.id === this.data.mobileProductInCart.voiceProductId
    );
    this.subscriptions$.push(
      this.store.dispatch(new RemoveMobileProductAction(this.data.mobileProductInCart.id)).subscribe(() => {
        if (this.data.mobileProductInCart.existingAgreementId) {
          this.subscriptions$.push(
            this.store
              .dispatch(
                new AddMobilePhoneReplacementAction(
                  mobilePhoneOffering,
                  mobilePhoneDetails,
                  this.data.mobileProductInCart.existingAgreementId,
                  this.data.isManualRegistration
                )
              )
              .subscribe(() => {
                const openNewVoiceSubscription =
                  mobilePhoneDetails.newOfferingId && !mobilePhoneDetails.isSubscriptionUpgrade;
                this.dialogRef.close(openNewVoiceSubscription);
              })
          );
        } else {
          this.subscriptions$.push(
            this.store
              .dispatch(
                new AddNewMobilePhoneAction(
                  mobilePhoneOffering,
                  mobilePhoneDetails,
                  this.data.isManualRegistration,
                  null,
                  connectedVoiceProductInCart?.isExistingSubscription ? null : connectedVoiceProductInCart
                )
              )
              .subscribe(() => {
                const openNewVoiceSubscription =
                  mobilePhoneDetails.newOfferingId && !mobilePhoneDetails.isSubscriptionUpgrade;
                this.dialogRef.close(openNewVoiceSubscription);
              })
          );
        }
      })
    );
  }

  get mobilePhoneVariantGroupsToDisplay(): HardwareVariantGroup[] {
    return this.filteredMobilePhoneVariantGroups || this.allMobilePhoneVariantGroups;
  }

  get mobilePhoneGroupsCount(): number {
    return this.mobilePhoneVariantGroupsToDisplay.length;
  }

  onBackToSearch(): void {
    this.selectedMobilePhoneVariant = null;

    afterNextRender(
      () => {
        this.dialogContent?.nativeElement.scrollTo({ top: this.hardwareSearchScrollPosition });
      },
      { injector: this.injector }
    );
  }

  onGroupSelected(variantGroup: HardwareVariantGroup) {
    this.loadingGroupVariantsForGroup = variantGroup.groupSlug;
    this.hardwareSearchScrollPosition = this.dialogContent?.nativeElement.scrollTop || 0;

    this.subscriptions$.push(
      this.mobilePhoneSearchService.getMobilePhoneVariants(variantGroup.groupSlug).subscribe(
        mobileVariants => {
          this.selectedGroupVariants = mobileVariants;

          this.selectedMobilePhoneVariant ||=
            this.mobilePhoneSearchHelperService.getCheapestVariantAvailableForPurchaseFromGroupVariants(
              this.selectedGroupVariants
            );

          this.loadingGroupVariantsForGroup = null;
          this.changeDetectorRef.detectChanges();
        },
        () => {
          this.loadingGroupVariantsForGroup = null;
          this.notificationService.error(`Feil ved henting av detaljer for ${variantGroup.name}`);
        }
      )
    );
  }

  onFilterChange(formValue: any) {
    this.searchText = formValue.searchText;
    this.selectedManufacturers = formValue.manufacturers;
    this.selectedCategories = formValue.categories;
    this.selectedAvailableForList = formValue.availableFor;

    const hasSearchText = this.searchText.length > 0;
    const hasSelectedManufacturers = this.selectedManufacturers.length > 0;
    const hasSelectedCategories = this.selectedCategories.length > 0;
    const hasSelectedAvailableForList = this.selectedAvailableForList.length > 0;

    const searchTextFilter = x => (hasSearchText ? x.name.toLowerCase().includes(this.searchText.toLowerCase()) : true);
    const manufacturerFilter = x => (hasSelectedManufacturers ? this.selectedManufacturers.includes(x.manufacturer) : true);
    const categoryFilter = x => (hasSelectedCategories ? this.selectedCategories.includes(x.mainCategory) : true);
    const availableForFilter = x =>
      hasSelectedAvailableForList
        ? this.selectedAvailableForList.some(x => x === MobilePhoneAvailability.equipmentUpgrade) && x.hasMobilBytte
        : true;

    if (!hasSearchText && !hasSelectedManufacturers && !hasSelectedCategories && !hasSelectedAvailableForList) {
      this.filteredMobilePhoneVariantGroups = null;
    } else {
      const filtered = this.allMobilePhoneVariantGroups.filter(
        m => searchTextFilter(m) && manufacturerFilter(m) && categoryFilter(m) && availableForFilter(m)
      );

      this.filteredMobilePhoneVariantGroups = filtered;
    }

    this.setSelectedItemInSearchResults();
  }

  private setSelectedItemInSearchResults() {
    if (this.data.mobileProductInCart?.mobilePhoneVariant?.offeringId) {
      const groupSlug = this.data.mobileProductInCart.mobilePhoneVariant.groupSlug;
      this.allMobilePhoneVariantGroups = this.allMobilePhoneVariantGroups.map(mobilePhoneVariant => ({
        ...mobilePhoneVariant,
        isSelected: mobilePhoneVariant.groupSlug === groupSlug,
      }));
    }
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
